/**
 * Quote normalization service for API response transformation
 * Handles payload shape validation and field mapping from API to UI format
 */

import {
  buildCoverageDetailsFromApiQuote,
  buildCoverageDisplayRows,
  marketPillLabel,
  marketTypeLabel,
  normalizeShapeCoverages,
  subLimitsFromApiQuote,
} from "@/utils/quoteNormalization";
import { pricingFromApiQuote } from "@/services/quotePricing";
import { pickOptionFields } from "@/utils/carrierOptionLabel";

export {
  buildCoverageDetailsFromApiQuote,
  buildCoverageDisplayRows,
  marketPillLabel,
  marketTypeLabel,
  normalizeShapeCoverages,
};

/**
 * Normalize quote array - safely extract quotes from payload
 * @param {Object} payload - Payload potentially containing quotes
 * @returns {Array} - Array of quotes or empty array
 */
export function normalizeQuotesArray(payload) {
  return Array.isArray(payload?.quotes) ? payload.quotes : [];
}

/**
 * Ensure payload has proper quote shape - always returns object with quotes array
 * @param {Object} payload - Payload to normalize
 * @returns {Object} - Payload with guaranteed quotes array
 */
export function ensureQuotesPayloadShape(payload) {
  const base = payload && typeof payload === "object" ? { ...payload } : {};
  const quotes = Array.isArray(base.quotes) ? base.quotes : [];
  return { ...base, quotes };
}

/**
 * Check if status indicates quotes are being reviewed
 * Flexible matching for "ReviewingQuotes" status
 * @param {string} status - Status string to check
 * @returns {boolean} - True if status is reviewing quotes
 */
export function isReviewingQuotesStatus(status) {
  if (status == null || status === "") return false;
  const n = String(status)
    .replaceAll(/[\s_-]/g, "")
    .toLowerCase();
  return n === "reviewingquotes" || n === "reviewing";
}

/**
 * Normalize quote payload from API for display
 * - UWB uses carrierName/status; UI expects payToCarrierName/payToCarrierQuoteStatus
 * - quote_ready → quoted; other statuses (e.g. processing) pass through for status column
 * @param {Object} quotesPayload - Quotes payload from API
 * @returns {Object} - Normalized payload for display
 */
export function normalizeQuotesForDisplay(quotesPayload) {
  const shaped = ensureQuotesPayloadShape(quotesPayload);
  if (!shaped.quotes.length) {
    return shaped;
  }
  const quotes = shaped.quotes.map((q) => {
    const normalized = { ...q };

    if (!normalized.payToCarrierName && normalized.carrierName) {
      normalized.payToCarrierName = normalized.carrierName;
    }
    if (!normalized.payToCarrierQuoteStatus && normalized.status) {
      const s = String(normalized.status).toLowerCase();
      normalized.payToCarrierQuoteStatus = s === "quote_ready" ? "quoted" : normalized.status;
    }
    if (!normalized.payToCarrierOrganizationName && normalized.carrierName) {
      normalized.payToCarrierOrganizationName = normalized.carrierName;
    }

    const coverageLimit =
      normalized.claimLimit ?? normalized.policyLimit ?? normalized.claim_limit ?? normalized.policy_limit;
    if (coverageLimit != null) {
      normalized.claimLimit = coverageLimit;
    }

    const retention = normalized.perAggregateRetention ?? normalized.per_aggregate_retention;
    if (retention != null) {
      normalized.deductible = retention;
    }

    const premiumVal = normalized.totalPremium ?? normalized.premium ?? normalized.total_premium;
    if (premiumVal !== undefined && premiumVal !== null) {
      normalized.totalPremium = premiumVal;
    }

    return normalized;
  });
  return { ...shaped, quotes };
}

/** Map GET submission detail quote row → compare / modal quote shape. */
export function mapApiQuoteToCompareRow(q) {
  const detail = q?.detail || {};
  const subLimits = subLimitsFromApiQuote(q);
  return {
    platformQuoteId: q.platformQuoteId,
    targetCarrierOrganizationId: q.targetCarrierOrganizationId,
    carrier: q.carrierName,
    market: marketTypeLabel(q.marketType),
    marketType: q.marketType ?? null,
    amBestRating: q.amBestRating ?? null,
    premium: q.premiumAmount,
    coverageLimit: q.coverageLimitAmount,
    retention: q.deductibleAmount,
    quoteRanOn: q.quoteRanOn ?? "",
    bindEligible: q.bindEligible,
    bindInProgress: q.bindInProgress,
    bindStatus: q.bindStatus,
    rowStatus: q.rowStatus,
    uwbSubmissionSeenAt: q.uwbSubmissionSeenAt ?? null,
    declinationReason: q.declinationReason ?? null,
    withdrawnBy: q.withdrawnBy ?? null,
    // S2CWB-333: the bound policy, and UWB's reason when a bind failed.
    policyNumber: q.policyNumber ?? null,
    policyEffectiveDate: q.policyEffectiveDate ?? null,
    policyExpirationDate: q.policyExpirationDate ?? null,
    bindFailureReason: q.bindFailureReason ?? null,
    boundPremium: q.boundPremium ?? null,
    detail,
    coverageDetails: buildCoverageDetailsFromApiQuote(q),
    subLimits: subLimits || undefined,
    // S2BWB-274: the broker-facing pricing the Pricing dialog shows and edits.
    pricing: pricingFromApiQuote(q),
    // S2BWB-277: the SHAPE quote.json endorsement schedule the API read; [] when none.
    endorsements: Array.isArray(q.endorsements) ? q.endorsements : [],
    // S2BWB-276: one of a carrier's several options (only then are these present).
    ...pickOptionFields(q),
  };
}

/**
 * Determine if quick quote flow is active
 * True when submission row exists or returned quote rows present
 * @param {Object} data - Quote submission data
 * @returns {boolean} - True if quick quotes flow should be shown
 */
export function deriveQuickQuotesFlowActive(data) {
  if (!data || typeof data !== "object") return false;
  if (normalizeQuotesArray(data.quick_quotes).length > 0) return true;
  const sub = data.quick_quotes_submission;
  if (sub && typeof sub === "object" && Object.keys(sub).length > 0) {
    if (sub.status != null && String(sub.status).trim() !== "") return true;
  }
  return false;
}

/**
 * Determine which quote type tab to show first in customer quotes view
 * @param {Object} data - Quote submission data
 * @returns {string} - "quick" or "full" branch to emphasize
 */
export function deriveCustomerQuotesStoreBranch(data) {
  if (!data || typeof data !== "object") return "quick";
  const fullLen = normalizeQuotesArray(data.full_quotes).length;
  if (data.ready_for_full_quote || isReviewingQuotesStatus(data.full_quotes_submission?.status) || fullLen > 0) {
    return "full";
  }
  return "quick";
}

/**
 * Determine default tab for broker side panel after loading quotes
 * @param {Object} data - Quote submission data
 * @returns {string} - "quick-quotes" or "full-quotes" tab
 */
export function deriveBrokerSidePanelDefaultTab(data) {
  if (!data || typeof data !== "object") return "quick-quotes";
  const fullLen = normalizeQuotesArray(data.full_quotes).length;
  // Keep parity with the original inline broker sidepanel logic:
  // `ready_for_full_quote` is the primary signal to switch to full quotes, even before full quote rows exist.
  if (data.ready_for_full_quote || fullLen > 0 || isReviewingQuotesStatus(data.full_quotes_submission?.status)) {
    return "full-quotes";
  }
  return "quick-quotes";
}

export default {
  normalizeQuotesArray,
  ensureQuotesPayloadShape,
  isReviewingQuotesStatus,
  normalizeQuotesForDisplay,
  normalizeShapeCoverages,
  buildCoverageDetailsFromApiQuote,
  buildCoverageDisplayRows,
  mapApiQuoteToCompareRow,
  deriveQuickQuotesFlowActive,
  deriveCustomerQuotesStoreBranch,
  deriveBrokerSidePanelDefaultTab,
};
